// ============================================================================
// BESPOKIAN Health - Public Navigation Header
// Luxury pharmacy navigation with gold accents
// ============================================================================

import { useState, useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { href: '/how-it-works', label: 'How It Works' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/employers', label: 'For Employers' },
  { href: '/about', label: 'About' },
  { href: '/faq', label: 'FAQ' },
];

export function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouterState();

  // Scroll to top on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [router.location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentPath = router.location.pathname;

  // Scroll to top handler for link clicks
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
    } border-b border-line-gray`}>
      <nav className="max-w-7xl mx-auto px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="BESPOKIAN Home">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                <img src="/logo.svg" alt="BESPOKIAN" className="h-10 w-10 object-contain" />
              </div>
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-navy-900 font-display text-xl tracking-wide font-semibold">
                BESPOKIAN
              </span>
              <span className="text-muted-navy text-[10px] tracking-[0.3em] uppercase -mt-1">
                HEALTH
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors relative group ${
                  currentPath === link.href
                    ? 'text-navy-800'
                    : 'text-navy-500 hover:text-navy-800'
                }`}
              >
                {link.label}
                {currentPath === link.href && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gold-500" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:1-877-237-7679"
              className="flex items-center gap-2 text-sm text-navy-500 hover:text-navy-800 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="font-medium">1-877-BES-PORX</span>
            </a>
            <Link to="/login">
              <button className="px-4 py-2 text-sm font-medium text-navy-600 hover:text-navy-800 transition-colors">
                Log In
              </button>
            </Link>
            <Link
              to="/login"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#1e3a5f] text-white text-sm font-medium rounded no-underline hover:bg-[#2c4a6e] transition-colors"
            >
              Start Shopping
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg text-navy-500 hover:text-navy-800 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden border-t border-navy-100 bg-white overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className={`block px-4 py-3 text-sm font-medium tracking-wide transition-colors rounded-lg ${
                currentPath === link.href
                  ? 'text-navy-800 bg-gold-50'
                  : 'text-navy-500 hover:text-navy-800 hover:bg-navy-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 mt-4 border-t border-navy-100 space-y-3">
            <Link to="/login" className="block">
              <button className="w-full px-4 py-3 text-sm font-medium text-navy-600 border border-navy-200 hover:bg-navy-50 rounded-lg transition-colors">
                Log In
              </button>
            </Link>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1e3a5f] text-white text-sm font-medium rounded no-underline hover:bg-[#2c4a6e] transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

